// :)
/**
 * synkro disconnect — remove all Synkro hook entries from agent settings.
 *
 * Preserves any other hooks the user has (Corridor, Noma, custom).
 *
 * Normal uninstall removes everything — hooks, configs, container, image, and
 * ~/.synkro — EXCEPT the user's scan data (~/.synkro/pgdata and pgdata-backups).
 * `--purge` wipes that data too, after an explicit typed confirmation, leaving
 * the machine as if Synkro was never installed.
 *
 * Order matters: tear down LIVE runtime first (pueue task + tmux session +
 * spawned bun plugin), THEN strip config, THEN remove files. Reversing the
 * order leaves orphaned processes holding the TCP port and a tmux session
 * with claude running from a deleted cwd.
 */
import { existsSync, rmSync, readdirSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { spawnSync } from 'node:child_process';
import { createInterface } from 'node:readline';
import { detectAgents } from '../installer/agentDetect.js';
import { uninstallCCHooks } from '../installer/ccHookConfig.js';
import { uninstallCursorHooks } from '../installer/cursorHookConfig.js';
import { uninstallMcpConfig, uninstallCursorMcpConfig, uninstallClaudeDesktopMcpConfig } from '../installer/mcpConfig.js';
import { uninstallLocalCC } from '../local-cc/install.js';
import { dockerRemove, dockerStatus, dockerSafeStop, imageTag } from '../local-cc/dockerInstall.js';
import { uninstallRefreshAgent, needsKeychainBridge } from '../local-cc/macKeychain.js';

const SYNKRO_DIR = join(homedir(), '.synkro');

async function tearDownLocalCC(): Promise<void> {
  const docker = dockerStatus();
  if (docker.running) {
    await dockerSafeStop();
    console.log('✓ stopped synkro-server container');
  } else {
    console.log('· no synkro-server container running');
  }
  dockerRemove();
  console.log('✓ removed synkro-server container');

  // Remove the Docker image too — a later `synkro install` just re-pulls it.
  try {
    const image = imageTag();
    const r = spawnSync('docker', ['rmi', '-f', image], { encoding: 'utf-8', timeout: 30_000 });
    console.log(r.status === 0 ? `✓ removed Docker image ${image}` : '· no Docker image to remove');
  } catch { /* docker may not be installed */ }

  if (needsKeychainBridge()) {
    try { uninstallRefreshAgent(); console.log('✓ launchd refresh agent removed'); } catch { /* best-effort */ }
  }

  uninstallLocalCC();
  console.log('✓ cleaned ~/.claude.json entries');
}

function confirmPurge(): Promise<boolean> {
  console.log('⚠  WARNING — synkro uninstall --purge');
  console.log('   This permanently deletes ALL Synkro data on this machine,');
  console.log('   including every scan finding, telemetry record, and backup in');
  console.log('   ~/.synkro/pgdata and ~/.synkro/pgdata-backups. It cannot be undone.\n');
  if (!process.stdin.isTTY) {
    console.log('   Non-interactive shell — re-run in a terminal to confirm.');
    return Promise.resolve(false);
  }
  const rl = createInterface({ input: process.stdin, output: process.stdout });
  return new Promise((resolve) => {
    rl.question("   Type 'yes' to wipe everything (anything else cancels): ", (answer) => {
      rl.close();
      resolve(answer.trim().toLowerCase() === 'yes');
    });
  });
}

export async function disconnectCommand(args: string[] = []): Promise<void> {
  const purge = args.includes('--purge');

  if (purge && !(await confirmPurge())) {
    console.log('\nAborted — nothing was removed.');
    return;
  }

  console.log('\nSynkro uninstall starting...\n');

  // Tear down local-cc runtime FIRST so we don't leave orphaned processes.
  await tearDownLocalCC();

  const agents = detectAgents();
  let sawClaudeCode = false;
  for (const agent of agents) {
    if (agent.kind === 'claude_code') {
      sawClaudeCode = true;
      const removed = uninstallCCHooks(agent.settingsPath);
      console.log(`${removed ? '✓' : '·'} ${agent.name}: ${removed ? 'removed Synkro hook entries' : 'no Synkro hooks found'}`);
    } else if (agent.kind === 'cursor') {
      const removed = uninstallCursorHooks(agent.settingsPath);
      console.log(`${removed ? '✓' : '·'} ${agent.name}: ${removed ? 'removed Synkro hook entries' : 'no Synkro hooks found'}`);
    }
  }

  // Also remove the Synkro guardrails MCP server entries from config files
  // (this is the OTHER mcp server — the rule-suggestions one — distinct from
  // the local-cc channel server cleaned up above).
  if (sawClaudeCode) {
    const mcpRemoved = uninstallMcpConfig();
    console.log(`${mcpRemoved ? '✓' : '·'} MCP guardrails (CC): ${mcpRemoved ? 'removed from ~/.claude.json' : 'no entry found'}`);
  }
  {
    const cursorMcpRemoved = uninstallCursorMcpConfig();
    console.log(`${cursorMcpRemoved ? '✓' : '·'} MCP guardrails (Cursor): ${cursorMcpRemoved ? 'removed from ~/.cursor/mcp.json' : 'no entry found'}`);
  }
  {
    const desktopMcpRemoved = uninstallClaudeDesktopMcpConfig();
    console.log(`${desktopMcpRemoved ? '✓' : '·'} MCP guardrails (Claude Desktop): ${desktopMcpRemoved ? 'removed from claude_desktop_config.json' : 'no entry found'}`);
  }

  // Filesystem cleanup. Normal uninstall removes everything except the user's
  // scan data (pgdata + pgdata-backups); --purge removes that too.
  if (existsSync(SYNKRO_DIR)) {
    if (purge) {
      rmSync(SYNKRO_DIR, { recursive: true, force: true });
      console.log(`✓ wiped ${SYNKRO_DIR} entirely — including all scan data and backups`);
    } else {
      const keep = new Set([join(SYNKRO_DIR, 'pgdata'), join(SYNKRO_DIR, 'pgdata-backups')]);
      const preserved: string[] = [];
      for (const entry of readdirSync(SYNKRO_DIR)) {
        const full = join(SYNKRO_DIR, entry);
        if (keep.has(full)) { preserved.push(entry); continue; }
        rmSync(full, { recursive: true, force: true });
      }
      if (preserved.length > 0) {
        console.log(`✓ removed Synkro config from ${SYNKRO_DIR} (kept your scan data: ${preserved.join(', ')})`);
        console.log('  run `synkro uninstall --purge` to delete that too');
      } else {
        console.log(`✓ removed ${SYNKRO_DIR}`);
      }
    }
  } else {
    console.log(`· ${SYNKRO_DIR} already gone`);
  }

  console.log(purge
    ? '\nSynkro fully removed — this machine is clean, as if it was never installed.'
    : '\nSynkro uninstalled. Your scan data is preserved.');
}
